import type { User } from '@supabase/supabase-js';
import { normalizeVisibleEmail } from '@/lib/portalAuthIdentity';

type RpcResult = {
  ready?: boolean;
  reason?: string;
  organization_onboarding_required?: boolean;
  repaired?: boolean;
};

type RpcResponse = {
  data: unknown;
  error: unknown;
};

export type ForkHireEmailProvisioningClient = {
  rpc: (fn: string, args: Record<string, unknown>) => PromiseLike<RpcResponse>;
};

export type ForkHireEmailProvisioningResult =
  | { ok: true; code: string; redirectTo: string; repaired: boolean }
  | { ok: false; code: string; message: string };

type ProvisioningInput = {
  visibleEmail?: string | null;
  fullName?: string | null;
};

const PUBLIC_PROVISIONING_ERROR = 'We could not finish preparing your ForkHire account. Please try again.';
const INTERNAL_AUTH_DOMAIN = 'auth.fork.internal';

function parseRpcResult(value: unknown): RpcResult {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as RpcResult) : {};
}

function isSafeVisibleEmail(value?: string | null) {
  if (!value) return false;
  const normalized = normalizeVisibleEmail(value);
  return normalized !== '' && !normalized.includes(INTERNAL_AUTH_DOMAIN) && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalized);
}

function getSafeVisibleEmail(user: User, inputEmail?: string | null) {
  const metadataVisibleEmail =
    typeof user.user_metadata?.visible_email === 'string' ? user.user_metadata.visible_email : null;
  const candidates = [inputEmail, metadataVisibleEmail, user.email];
  const safeEmail = candidates.find(isSafeVisibleEmail);
  return safeEmail ? normalizeVisibleEmail(safeEmail) : '';
}

function getFullName(user: User, inputFullName?: string | null) {
  if (inputFullName?.trim()) return inputFullName.trim();
  if (typeof user.user_metadata?.full_name === 'string' && user.user_metadata.full_name.trim()) {
    return user.user_metadata.full_name.trim();
  }
  if (typeof user.user_metadata?.name === 'string' && user.user_metadata.name.trim()) {
    return user.user_metadata.name.trim();
  }
  return '';
}

function safeLog(code: string, detail?: Record<string, unknown>) {
  console.error(code, detail ?? {});
}

export function isForkHireEmailVerificationRequiredError(error: unknown) {
  if (!error || typeof error !== 'object') return false;
  const authError = error as { code?: string; status?: number; message?: string };
  const code = authError.code?.toLowerCase() ?? '';
  const message = authError.message?.toLowerCase() ?? '';

  return (
    code === 'email_not_confirmed' ||
    code === 'email_not_verified' ||
    message.includes('email not confirmed') ||
    message.includes('email not verified') ||
    message.includes('confirm your email') ||
    message.includes('verify your email')
  );
}

export async function prepareForkHireEmailPasswordAccount(
  client: ForkHireEmailProvisioningClient,
  user: User,
  input: ProvisioningInput = {},
): Promise<ForkHireEmailProvisioningResult> {
  const visibleEmail = getSafeVisibleEmail(user, input.visibleEmail);
  if (!visibleEmail) {
    safeLog('forkhire_email_provisioning_failed', { reason: 'missing_visible_email' });
    return { ok: false, code: 'forkhire_email_provisioning_failed', message: PUBLIC_PROVISIONING_ERROR };
  }

  const { data, error } = await client.rpc('prepare_forkhire_email_password_account', {
    _visible_email: visibleEmail,
    _full_name: getFullName(user, input.fullName),
  });
  const result = parseRpcResult(data);

  if (error || result.ready !== true) {
    safeLog('forkhire_email_provisioning_failed', {
      reason: result.reason ?? 'rpc_error',
      hasRpcError: Boolean(error),
    });
    return { ok: false, code: 'forkhire_email_provisioning_failed', message: PUBLIC_PROVISIONING_ERROR };
  }

  if (result.organization_onboarding_required || result.reason === 'organization_onboarding_required') {
    safeLog('forkhire_email_org_onboarding_required');
    return {
      ok: true,
      code: 'forkhire_email_org_onboarding_required',
      redirectTo: '/hire/complete-company-setup',
      repaired: Boolean(result.repaired),
    };
  }

  if (result.repaired) {
    safeLog('forkhire_email_partial_account_repaired');
  }

  return {
    ok: true,
    code: result.repaired ? 'forkhire_email_partial_account_repaired' : 'forkhire_email_provisioning_ready',
    redirectTo: '/dashboard',
    repaired: Boolean(result.repaired),
  };
}
